import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../../services/authService';
import candidateService from '../../services/candidateService';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterPanel from './components/FilterPanel';
import CandidateTable from './components/CandidateTable';
import CandidateCards from './components/CandidateCards';
import CandidateModal from './components/CandidateModal';
import BulkActionsDropdown from './components/BulkActionsDropdown';
import ViewToggle from './components/ViewToggle';
import ExportDropdown from './components/ExportDropdown';
import { hasActionAccess, hasFeatureAccess } from '../../utils/roleBasedAccess';

const CandidateDatabase = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Authentication and user state
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state (removed isSidebarCollapsed as FixedSidebar is always fixed)
  const [currentView, setCurrentView] = useState('table');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'applicationDate', direction: 'desc' });
  
  // Data state
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    jobCategories: [],
    experienceLevels: [],
    locations: [],
    minScore: '',
    maxScore: '',
    dateFrom: '',
    dateTo: ''
  });

  // Load initial data
  useEffect(() => {
    const initializeData = async () => {
      console.log('[CandidateDatabase] Initializing data');
      setLoading(true);
      setError(null);
      
      try {
        // Get current user
        const user = authService.getStoredUser() || await authService.getCurrentUser();
        setCurrentUser(user);
        console.log('[CandidateDatabase] Current user loaded:', user.email);
        
        // Load candidates
        await loadCandidates();
        
      } catch (error) {
        console.error('[CandidateDatabase] Error initializing data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Load candidates from backend
  const loadCandidates = async (options = {}) => {
    try {
      console.log('[CandidateDatabase] Loading candidates from backend');
      
      const queryOptions = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...options
      };
      
      const result = await candidateService.getCandidates(queryOptions);
      console.log('[CandidateDatabase] Candidates loaded:', result);
      
      if (result) {
        setCandidates(result.candidates || result.data || []);
        setPagination(prev => ({
          ...prev,
          total: result.total || 0
        }));
      }
    } catch (error) {
      console.error('[CandidateDatabase] Error loading candidates:', error);
      // Fallback to empty array on error
      setCandidates([]);
      throw error;
    }
  };

  // Calculate notification counts from real data
  const notificationCounts = {
    candidates: candidates.filter(c => c.status === 'new').length,
    processing: candidates.filter(c => c.status === 'screening').length,
    interviews: candidates.filter(c => c.status === 'interview').length,
    total: candidates.filter(c => ['new', 'screening', 'interview'].includes(c.status)).length
  };

  // Filter and sort candidates when data or filters change
  useEffect(() => {
    if (!candidates.length) {
      setFilteredCandidates([]);
      return;
    }

    let filtered = [...candidates];

    // Apply search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(candidate =>
        candidate?.name?.toLowerCase()?.includes(searchTerm) ||
        candidate?.email?.toLowerCase()?.includes(searchTerm) ||
        candidate?.position?.toLowerCase()?.includes(searchTerm) ||
        candidate?.department?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Apply job category filter
    if (filters?.jobCategories?.length > 0) {
      filtered = filtered?.filter(candidate => {
        const categoryMap = {
          'software-engineering': ['Frontend Developer', 'Backend Developer', 'DevOps Engineer'],
          'data-science': ['Data Scientist'],
          'product-management': ['Product Manager'],
          'design': ['UX Designer'],
          'marketing': ['Marketing Manager'],
          'sales': ['Sales Manager']
        };
        
        return filters?.jobCategories?.some(category =>
          categoryMap?.[category]?.some(title =>
            candidate?.position?.includes(title)
          )
        );
      });
    }

    // Apply experience level filter
    if (filters?.experienceLevels?.length > 0) {
      filtered = filtered?.filter(candidate => {
        const exp = candidate?.experience;
        return filters?.experienceLevels?.some(level => {
          switch (level) {
            case 'entry': return exp <= 2;
            case 'mid': return exp >= 3 && exp <= 5;
            case 'senior': return exp >= 6 && exp <= 10;
            case 'lead': return exp > 10;
            default: return false;
          }
        });
      });
    }

    // Apply location filter
    if (filters?.locations?.length > 0) {
      filtered = filtered?.filter(candidate =>
        filters?.locations?.some(location => {
          if (location === 'remote') return candidate?.location === 'Remote';
          return candidate?.location?.includes(location?.split('-')?.map(word => 
            word?.charAt(0)?.toUpperCase() + word?.slice(1)
          )?.join(' '));
        })
      );
    }

    // Apply score range filter
    if (filters?.minScore) {
      filtered = filtered?.filter(candidate => candidate?.aiScore >= parseInt(filters?.minScore));
    }
    if (filters?.maxScore) {
      filtered = filtered?.filter(candidate => candidate?.aiScore <= parseInt(filters?.maxScore));
    }

    // Apply date range filter
    if (filters?.dateFrom) {
      filtered = filtered?.filter(candidate => 
        new Date(candidate.applicationDate) >= new Date(filters.dateFrom)
      );
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter(candidate => 
        new Date(candidate.applicationDate) <= new Date(filters.dateTo)
      );
    }

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'applicationDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredCandidates(filtered);
  }, [candidates, filters, sortConfig]);

  // Handle URL parameters for filtering
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const status = searchParams?.get('status');
    const filter = searchParams?.get('filter');

    if (status || filter) {
      let newFilters = { ...filters };
      
      // Handle status-based filtering
      if (status === 'active') {
        newFilters.search = '';
        // Filter for active statuses
      } else if (status === 'pending') {
        newFilters.search = '';
        // Filter for pending review
      } else if (status === 'interview-ready') {
        newFilters.search = '';
        // Filter for interview ready candidates
      }

      // Handle special filters
      if (filter === 'top-matches') {
        newFilters.minScore = '85';
      }

      setFilters(newFilters);
    }
  }, [location?.search]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig?.key === key && prevConfig?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setIsModalOpen(true);
  };

  const handleQuickAction = (action, candidate) => {
    switch (action) {
      case 'view':
        navigate(`/candidate-profile-detail?id=${candidate?.id}`);
        break;
      case 'interview':
        navigate(`/interview-scheduling?candidateId=${candidate?.id}`);
        break;
      case 'message': console.log('Send message to:', candidate?.name);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'for candidates:', selectedCandidates);
    // Implement bulk actions here
  };

  const handleExport = (format, type) => {
    console.log('Export candidates as:', format, type);
    // Implement export functionality here
  };

  const handleScheduleInterview = (candidate) => {
    navigate(`/interview-scheduling?candidateId=${candidate?.id}`);
    setIsModalOpen(false);
  };

  const handleSendMessage = (candidate) => {
    console.log('Send message to:', candidate?.name);
    setIsModalOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      jobCategories: [],
      experienceLevels: [],
      locations: [],
      minScore: '',
      maxScore: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading candidates...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Candidates</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        currentUser={currentUser}
        notificationCounts={notificationCounts}
        onNavigate={(path) => navigate(path)}
      />
      <FixedSidebar currentPath={location?.pathname} />
      <main className="ml-72 pt-16">
        <div className="p-6">
          <Breadcrumb customBreadcrumbs={[
            { label: 'Dashboard', href: '/' },
            { label: 'Candidate Database', href: '/candidate-database' }
          ]} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Candidate Database</h1>
              <p className="text-muted-foreground">
                Search, filter, and manage processed candidate profiles with AI-generated insights
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {/* Upload CVs - Only for Admin, HR Manager, Recruiter */}
              {hasFeatureAccess(currentUser?.role, 'cv-processing') && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/cv-upload-processing')}
                  iconName="Upload"
                  iconSize={16}
                >
                  Upload CVs
                </Button>
              )}
              
              {/* Schedule Interview - Only for roles with interview scheduling access */}
              {hasActionAccess(currentUser?.role, 'interviews', 'schedule') && (
                <Button
                  variant="default"
                  onClick={() => navigate('/interview-scheduling')}
                  iconName="Calendar"
                  iconSize={16}
                >
                  Schedule Interview
                </Button>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Candidates</p>
                  <p className="text-2xl font-bold text-foreground">{candidates?.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} color="#3B82F6" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">High Scores (85+)</p>
                  <p className="text-2xl font-bold text-foreground">
                    {candidates?.filter(c => c?.aiScore >= 85)?.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} color="#10B981" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">In Interview</p>
                  <p className="text-2xl font-bold text-foreground">
                    {candidates?.filter(c => c?.status === 'interview')?.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Icon name="Calendar" size={24} color="#8B5CF6" />
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Hired</p>
                  <p className="text-2xl font-bold text-foreground">
                    {candidates?.filter(c => c?.status === 'hired')?.length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Icon name="UserCheck" size={24} color="#059669" />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Filter Panel */}
            <div className="lg:col-span-3">
              <FilterPanel
                filters={filters}
                onFiltersChange={setFilters}
                onClearFilters={clearFilters}
              />
            </div>

            {/* Candidates List */}
            <div className="lg:col-span-9">
              {/* Toolbar */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                  <ViewToggle
                    currentView={currentView}
                    onViewChange={setCurrentView}
                  />
                  <div className="text-sm text-muted-foreground">
                    {filteredCandidates?.length} of {candidates?.length} candidates
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {/* Bulk Actions - Only for roles with candidate edit/delete access */}
                  {(hasActionAccess(currentUser?.role, 'candidateActions', 'edit') || 
                    hasActionAccess(currentUser?.role, 'candidateActions', 'delete')) && (
                    <BulkActionsDropdown
                      selectedCount={selectedCandidates?.length}
                      onBulkAction={handleBulkAction}
                      userRole={currentUser?.role}
                    />
                  )}
                  
                  {/* Export - Only for roles with export access */}
                  {hasActionAccess(currentUser?.role, 'candidateActions', 'export') && (
                    <ExportDropdown onExport={handleExport} />
                  )}
                </div>
              </div>

              {/* Candidates Display */}
              {currentView === 'table' ? (
                <CandidateTable
                  candidates={filteredCandidates}
                  selectedCandidates={selectedCandidates}
                  onSelectionChange={setSelectedCandidates}
                  onCandidateClick={handleCandidateClick}
                  onQuickAction={handleQuickAction}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  userRole={currentUser?.role}
                />
              ) : (
                <CandidateCards
                  candidates={filteredCandidates}
                  selectedCandidates={selectedCandidates}
                  onSelectionChange={setSelectedCandidates}
                  onCandidateClick={handleCandidateClick}
                  onQuickAction={handleQuickAction}
                  userRole={currentUser?.role}
                />
              )}
            </div>
          </div>
        </div>
      </main>
      {/* Candidate Detail Modal */}
      <CandidateModal
        candidate={selectedCandidate}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onScheduleInterview={handleScheduleInterview}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

export default CandidateDatabase;